import { getColorVariable } from "@components/common/utils/helper";
import { type ButtonProps } from "./button";
import { KeyValueObject } from "@components/common/types/general";

export function getBackgroundColor(
    active: ButtonProps["active"],
    text: ButtonProps["text"],
    outline: ButtonProps["outline"],
    underline: ButtonProps["underline"],
    outlineKeepOnHover: ButtonProps["outlineKeepOnHover"],
    color: ButtonProps["color"],
    fillIcon: ButtonProps["fillIcon"],
    clean: ButtonProps["clean"],
    theme: any,
): KeyValueObject | null {
    if (!text && !fillIcon && !clean) {
        if (active && !underline && !outlineKeepOnHover) {
            return {
                backgroundColor: getColorVariable(color, theme, {
                    gray: theme.colors.gray[600],
                    white: theme.colors.gray[100],
                    colorize: theme.colors[color || "primary"][700],
                    default: theme.colors.gray[700],
                }),
            };
        }
        if (outline || underline) {
            return null;
        }
        return {
            backgroundColor: getColorVariable(color, theme, {
                gray: theme.colors.gray[400],
                white: theme.colors.white,
                colorize: theme.colors[color || "primary"][500],
                default: theme.colors.gray[950],
            }),
        };
    }
    return null;
}

export function getFontSize(size: ButtonProps["size"], theme: any): KeyValueObject {
    if (size === "xs" || size === "sm") {
        return {
            fontSize: theme.fontSize["3xs"],
        };
    }
    return {
        fontSize: theme.fontSize.xs,
    };
}

export function getPaddingX(
    underline: ButtonProps["underline"],
    size: ButtonProps["size"],
    label: ButtonProps["label"],
    text: ButtonProps["text"],
    fillIcon: ButtonProps["fillIcon"],
    clean: ButtonProps["clean"],
    theme: any,
): KeyValueObject | null {
    if (!text && !fillIcon && !clean) {
        if (underline) {
            if (size === "lg") {
                if (!label) {
                    return {
                        paddingLeft: theme.spacing[0],
                        paddingRight: theme.spacing[0],
                        [`@media (min-width: ${theme.screens.lg})`]: {
                            paddingLeft: theme.spacing[3],
                            paddingRight: theme.spacing[3],
                        },
                    };
                }
                return {
                    paddingLeft: theme.spacing[0],
                    paddingRight: theme.spacing[0],
                    [`@media (min-width: ${theme.screens.lg})`]: {
                        paddingLeft: theme.spacing[8],
                        paddingRight: theme.spacing[8],
                    },
                };
            }
            return {
                paddingLeft: theme.spacing[0],
                paddingRight: theme.spacing[0],
            };
        }
        if (size === "xs") {
            if (!label) {
                return {
                    paddingLeft: theme.spacing[0],
                    paddingRight: theme.spacing[0],
                };
            }
            return {
                paddingLeft: theme.spacing[2],
                paddingRight: theme.spacing[2],
            };
        }
        if (size === "sm") {
            if (!label) {
                return {
                    paddingLeft: theme.spacing[0],
                    paddingRight: theme.spacing[0],
                };
            }
            return {
                paddingLeft: theme.spacing[3],
                paddingRight: theme.spacing[3],
            };
        }
        if (size === "lg") {
            if (!label) {
                return {
                    paddingLeft: theme.spacing[1.5],
                    paddingRight: theme.spacing[1.5],
                    [`@media (min-width: ${theme.screens.lg})`]: {
                        paddingLeft: theme.spacing[3],
                        paddingRight: theme.spacing[3],
                    },
                };
            }
            return {
                paddingLeft: theme.spacing[5],
                paddingRight: theme.spacing[5],
                [`@media (min-width: ${theme.screens.lg})`]: {
                    paddingLeft: theme.spacing[8],
                    paddingRight: theme.spacing[8],
                },
            };
        }
        if (!label) {
            return {
                paddingLeft: "3px",
                paddingRight: "3px",
            };
        }
        return {
            paddingLeft: theme.spacing[5],
            paddingRight: theme.spacing[5],
        };
    }
    return null;
}

export function getPaddingY(
    underline: ButtonProps["underline"],
    size: ButtonProps["size"],
    fillIcon: ButtonProps["fillIcon"],
    text: ButtonProps["text"],
    disableHeight: ButtonProps["disableHeight"],
    theme: any,
): KeyValueObject | null {
    if (fillIcon || (text && disableHeight)) {
        return null;
    }
    if ((underline && size === "lg") || size === "lg") {
        return {
            paddingTop: theme.spacing[1.5],
            paddingBottom: theme.spacing[1.5],
            [`@media (min-width: ${theme.screens.lg})`]: {
                paddingTop: theme.spacing[3],
                paddingBottom: theme.spacing[3],
            },
        };
    }
    if (size === "xs" || size === "sm") {
        return {
            paddingTop: theme.spacing[0],
            paddingBottom: theme.spacing[0],
        };
    }
    return {
        paddingTop: "3px",
        paddingBottom: "3px",
    };
}

export function getBorderRadius(rounded: ButtonProps["rounded"], theme: any): KeyValueObject | null {
    if (rounded === "md") {
        return { borderRadius: theme.borderRadius.DEFAULT };
    }
    if (rounded === "lg") {
        return { borderRadius: theme.borderRadius.lg };
    }
    if (rounded === "full") {
        return { borderRadius: theme.borderRadius.full };
    }
    if (rounded === "l-md") {
        return { borderTopLeftRadius: theme.borderRadius.DEFAULT, borderBottomLeftRadius: theme.borderRadius.DEFAULT };
    }
    if (rounded === "r-md") {
        return { borderTopRightRadius: theme.borderRadius.DEFAULT, borderBottomRightRadius: theme.borderRadius.DEFAULT };
    }
    if (rounded === "l-lg") {
        return { borderTopLeftRadius: theme.borderRadius.lg, borderBottomLeftRadius: theme.borderRadius.lg };
    }
    if (rounded === "r-lg") {
        return { borderTopRightRadius: theme.borderRadius.lg, borderBottomRightRadius: theme.borderRadius.lg };
    }
    if (rounded === "l-full") {
        return { borderTopLeftRadius: theme.borderRadius.full, borderBottomLeftRadius: theme.borderRadius.full };
    }
    if (rounded === "r-full") {
        return { borderTopRightRadius: theme.borderRadius.full, borderBottomRightRadius: theme.borderRadius.full };
    }
    return null;
}

export function getColor(
    active: ButtonProps["active"],
    text: ButtonProps["text"],
    outline: ButtonProps["outline"],
    underline: ButtonProps["underline"],
    outlineKeepOnHover: ButtonProps["outlineKeepOnHover"],
    color: ButtonProps["color"],
    fillIcon: ButtonProps["fillIcon"],
    clean: ButtonProps["clean"],
    keepColor: ButtonProps["keepColor"],
    theme: any,
): KeyValueObject {
    if (keepColor) return theme.colors.gray[950];
    if (underline || (outline && outlineKeepOnHover) || text || fillIcon || clean) {
        if (active && !fillIcon) {
            return {
                color: getColorVariable(color, theme, {
                    gray: theme.colors.gray[600],
                    white: theme.colors.gray[100],
                    colorize: theme.colors[color || "primary"][700],
                    default: theme.colors.gray[700],
                }),
            };
        } else {
            return {
                color: getColorVariable(color, theme, {
                    gray: theme.colors.gray[400],
                    white: theme.colors.white,
                    colorize: theme.colors[color || "primary"][500],
                    default: theme.colors.gray[950],
                }),
            };
        }
    }
    if (outline) {
        if (!active) {
            return {
                color: getColorVariable(color, theme, {
                    gray: theme.colors.gray[400],
                    white: theme.colors.white,
                    colorize: theme.colors[color || "primary"][500],
                    default: theme.colors.gray[950],
                }),
            };
        }
    }
    return {
        color: getColorVariable(color, theme, {
            gray: theme.colors.white,
            white: theme.colors.gray[950],
            colorize: theme.colors.white,
            default: theme.colors.white,
        }),
    };
}

export function getBorderColor(
    active: ButtonProps["active"],
    text: ButtonProps["text"],
    color: ButtonProps["color"],
    clean: ButtonProps["clean"],
    theme: any,
): KeyValueObject | null {
    if (text || clean) {
        return {
            borderColor: "transparent",
        };
    }
    if (active) {
        return {
            borderColor: getColorVariable(color, theme, {
                gray: theme.colors.gray[600],
                white: theme.colors.gray[100],
                colorize: theme.colors[color || "primary"][700],
                default: theme.colors.gray[700],
            }),
        };
    }
    return {
        borderColor: getColorVariable(color, theme, {
            gray: theme.colors.gray[400],
            white: theme.colors.white,
            colorize: theme.colors[color || "primary"][500],
            default: theme.colors.gray[950],
        }),
    };
}

export function getArrowBeforeBackgroundColor(
    active: ButtonProps["active"],
    text: ButtonProps["text"],
    outline: ButtonProps["outline"],
    underline: ButtonProps["underline"],
    outlineKeepOnHover: ButtonProps["outlineKeepOnHover"],
    color: ButtonProps["color"],
    keepColor: ButtonProps["keepColor"],
    theme: any,
): KeyValueObject {
    if (keepColor) return theme.colors.gray[950];
    if (underline || (outline && outlineKeepOnHover) || text) {
        if (active) {
            return {
                backgroundColor: getColorVariable(color, theme, {
                    gray: theme.colors.gray[600],
                    white: theme.colors.gray[100],
                    colorize: theme.colors[color || "primary"][700],
                    default: theme.colors.gray[700],
                }),
            };
        } else {
            return {
                backgroundColor: getColorVariable(color, theme, {
                    gray: theme.colors.gray[400],
                    white: theme.colors.white,
                    colorize: theme.colors[color || "primary"][500],
                    default: theme.colors.gray[950],
                }),
            };
        }
    }
    if (outline) {
        if (!active) {
            return {
                backgroundColor: getColorVariable(color, theme, {
                    gray: theme.colors.gray[400],
                    white: theme.colors.white,
                    colorize: theme.colors[color || "primary"][500],
                    default: theme.colors.gray[950],
                }),
            };
        }
    }
    return {
        backgroundColor: getColorVariable(color, theme, {
            gray: theme.colors.white,
            white: theme.colors.gray[950],
            colorize: theme.colors.white,
            default: theme.colors.white,
        }),
    };
}

export function getArrowSpanBeforeBackgroundColor(
    active: ButtonProps["active"],
    text: ButtonProps["text"],
    outline: ButtonProps["outline"],
    underline: ButtonProps["underline"],
    outlineKeepOnHover: ButtonProps["outlineKeepOnHover"],
    color: ButtonProps["color"],
    keepColor: ButtonProps["keepColor"],
    theme: any,
): KeyValueObject {
    if (keepColor) return { backgroundColor: theme.colors.gray[950] };
    if (underline || (outline && outlineKeepOnHover) || text) {
        if (active) {
            return {
                backgroundColor: getColorVariable(color, theme, {
                    gray: theme.colors.gray[600],
                    white: theme.colors.gray[100],
                    colorize: theme.colors[color || "primary"][700],
                    default: theme.colors.gray[700],
                }),
            };
        } else {
            return {
                backgroundColor: getColorVariable(color, theme, {
                    gray: theme.colors.gray[400],
                    white: theme.colors.white,
                    colorize: theme.colors[color || "primary"][500],
                    default: theme.colors.gray[950],
                }),
            };
        }
    }
    return {
        backgroundColor: getColorVariable(color, theme, {
            gray: theme.colors.white,
            white: theme.colors.gray[950],
            colorize: theme.colors.white,
            default: theme.colors.white,
        }),
    };
}

export function getSpinnerBorderColor(
    active: ButtonProps["active"],
    text: ButtonProps["text"],
    outline: ButtonProps["outline"],
    underline: ButtonProps["underline"],
    outlineKeepOnHover: ButtonProps["outlineKeepOnHover"],
    color: ButtonProps["color"],
    keepColor: ButtonProps["keepColor"],
    theme: any,
): KeyValueObject {
    if (keepColor) {
        return {
            borderColor: `${getColorVariable(color, theme, {
                gray: theme.colors.gray[950],
                white: theme.colors.gray[950],
                colorize: theme.colors.gray[950],
                default: theme.colors.gray[950],
            })} ${getColorVariable(color, theme, {
                gray: theme.colors.gray[950],
                white: theme.colors.gray[950],
                colorize: theme.colors.gray[950],
                default: theme.colors.gray[950],
            })} ${getColorVariable(color, theme, {
                gray: theme.colors.gray[950],
                white: theme.colors.gray[950],
                colorize: theme.colors.gray[950],
                default: theme.colors.gray[950],
            })} transparent;`,
        };
    }
    if (underline || (outline && outlineKeepOnHover) || text) {
        if (active) {
            return {
                borderColor: `${getColorVariable(color, theme, {
                    gray: theme.colors.gray[600],
                    white: theme.colors.gray[100],
                    colorize: theme.colors[color || "primary"][700],
                    default: theme.colors.gray[700],
                })} ${getColorVariable(color, theme, {
                    gray: theme.colors.gray[600],
                    white: theme.colors.gray[100],
                    colorize: theme.colors[color || "primary"][700],
                    default: theme.colors.gray[700],
                })} ${getColorVariable(color, theme, {
                    gray: theme.colors.gray[600],
                    white: theme.colors.gray[100],
                    colorize: theme.colors[color || "primary"][700],
                    default: theme.colors.gray[700],
                })} transparent;`,
            };
        } else {
            return {
                borderColor: `${getColorVariable(color, theme, {
                    gray: theme.colors.gray[400],
                    white: theme.colors.white,
                    colorize: theme.colors[color || "primary"][500],
                    default: theme.colors.gray[950],
                })} ${getColorVariable(color, theme, {
                    gray: theme.colors.gray[400],
                    white: theme.colors.white,
                    colorize: theme.colors[color || "primary"][500],
                    default: theme.colors.gray[950],
                })} ${getColorVariable(color, theme, {
                    gray: theme.colors.gray[400],
                    white: theme.colors.white,
                    colorize: theme.colors[color || "primary"][500],
                    default: theme.colors.gray[950],
                })} transparent;`,
            };
        }
    }
    if (outline) {
        if (!active) {
            return {
                borderColor: `${getColorVariable(color, theme, {
                    gray: theme.colors.gray[400],
                    white: theme.colors.white,
                    colorize: theme.colors[color || "primary"][500],
                    default: theme.colors.gray[950],
                })} ${getColorVariable(color, theme, {
                    gray: theme.colors.gray[400],
                    white: theme.colors.white,
                    colorize: theme.colors[color || "primary"][500],
                    default: theme.colors.gray[950],
                })} ${getColorVariable(color, theme, {
                    gray: theme.colors.gray[400],
                    white: theme.colors.white,
                    colorize: theme.colors[color || "primary"][500],
                    default: theme.colors.gray[950],
                })} transparent;`,
            };
        }
    }
    return {
        borderColor: `${getColorVariable(color, theme, {
            gray: theme.colors.white,
            white: theme.colors.gray[950],
            colorize: theme.colors.white,
            default: theme.colors.white,
        })} ${getColorVariable(color, theme, {
            gray: theme.colors.white,
            white: theme.colors.gray[950],
            colorize: theme.colors.white,
            default: theme.colors.white,
        })} ${getColorVariable(color, theme, {
            gray: theme.colors.white,
            white: theme.colors.gray[950],
            colorize: theme.colors.white,
            default: theme.colors.white,
        })} transparent;`,
    };
}

export function getBoxShadow(shadow: ButtonProps["shadow"], color: ButtonProps["color"], theme: any): KeyValueObject | null {
    if (shadow) {
        const shadow = getColorVariable(color, theme, {
            gray: "button",
            white: "button",
            colorize: `button-${color}`,
            default: "button",
        });
        return {
            boxShadow: theme.boxShadow[shadow] || theme.boxShadow.DEFAULT,
        };
    }
    return null;
}

export function getBoxShadowFocus(
    color: ButtonProps["color"],
    text: ButtonProps["text"],
    underline: ButtonProps["underline"],
    fillIcon: ButtonProps["fillIcon"],
    clean: ButtonProps["clean"],
    focusState: ButtonProps["focusState"],
    theme: any,
): KeyValueObject | null {
    if (!text && !underline && !fillIcon && !clean && focusState !== "hover") {
        const shadow = getColorVariable(color, theme, {
            gray: "button-focus",
            white: "button-focus",
            colorize: `button-${color}-focus`,
            default: "button-focus",
        });
        return {
            boxShadow: theme.boxShadow[shadow] || theme.boxShadow.DEFAULT,
        };
    }
    return null;
}

export function getAdornmentsSvgSize(size: ButtonProps["size"], theme: any): KeyValueObject {
    if (size === "xs") {
        return {
            width: theme.spacing[3],
            height: theme.spacing[3],
        };
    }
    if (size === "sm") {
        return {
            width: theme.spacing[3.5],
            height: theme.spacing[3.5],
        };
    }
    if (size === "lg") {
        return {
            width: theme.spacing[5],
            height: theme.spacing[5],
        };
    }
    return {
        width: theme.spacing[4],
        height: theme.spacing[4],
    };
}

export function getBodyHeight(
    size: ButtonProps["size"],
    text: ButtonProps["text"],
    disableHeight: ButtonProps["disableHeight"],
    theme: any,
): KeyValueObject | null {
    if (text && disableHeight) return null;
    if (size === "xs") {
        return {
            minHeight: theme.spacing[5],
        };
    }
    return {
        minHeight: theme.spacing[6],
    };
}
